/** @jsxImportSource @emotion/react */
"use client";

import { keyframes } from "@emotion/react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as React from "react";

import type { ThemeType } from "../../../../global/emotion";
import { radius, spacing, stroke, zIndices } from "../../../../global/emotion";

// Animation keyframes
const fadeIn = keyframes({
  "0%": { opacity: 0 },
  "100%": { opacity: 1 },
});

const fadeOut = keyframes({
  "0%": { opacity: 1 },
  "100%": { opacity: 0 },
});

const zoomIn = keyframes({
  "0%": { transform: "scale(0.95)" },
  "100%": { transform: "scale(1)" },
});

const zoomOut = keyframes({
  "0%": { transform: "scale(1)" },
  "100%": { transform: "scale(0.95)" },
});

const slideInFromTop = keyframes({
  "0%": { transform: "translateY(-2px)" },
  "100%": { transform: "translateY(0)" },
});

const slideInFromBottom = keyframes({
  "0%": { transform: "translateY(2px)" },
  "100%": { transform: "translateY(0)" },
});

const slideInFromLeft = keyframes({
  "0%": { transform: "translateX(-2px)" },
  "100%": { transform: "translateX(0)" },
});

const slideInFromRight = keyframes({
  "0%": { transform: "translateX(2px)" },
  "100%": { transform: "translateX(0)" },
});

const contentStyle = (theme: ThemeType) => ({
  backgroundColor: theme.semanticColors.background,
  borderRadius: radius.ms,
  border: `${stroke.regular} solid ${theme.semanticColors.neutralsLowest}`,
  boxShadow: `0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)`,
  color: theme.semanticColors.primary,
  outline: "none",
  padding: spacing.s,
  pointerEvents: "auto" as const,
  transformOrigin: "var(--radix-popover-content-transform-origin)",
  zIndex: zIndices.tooltip,

  // Animations based on state
  "&[data-state='open']": {
    animation: `${fadeIn} 150ms cubic-bezier(0.16, 1, 0.3, 1), ${zoomIn} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  },
  "&[data-state='closed']": {
    animation: `${fadeOut} 150ms cubic-bezier(0.4, 0, 1, 1), ${zoomOut} 150ms cubic-bezier(0.4, 0, 1, 1)`,
  },

  // Side-specific animations
  "&[data-side='top'][data-state='open']": {
    animation: `${fadeIn} 150ms cubic-bezier(0.16, 1, 0.3, 1), ${zoomIn} 150ms cubic-bezier(0.16, 1, 0.3, 1), ${slideInFromBottom} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  },
  "&[data-side='bottom'][data-state='open']": {
    animation: `${fadeIn} 150ms cubic-bezier(0.16, 1, 0.3, 1), ${zoomIn} 150ms cubic-bezier(0.16, 1, 0.3, 1), ${slideInFromTop} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  },
  "&[data-side='left'][data-state='open']": {
    animation: `${fadeIn} 150ms cubic-bezier(0.16, 1, 0.3, 1), ${zoomIn} 150ms cubic-bezier(0.16, 1, 0.3, 1), ${slideInFromRight} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  },
  "&[data-side='right'][data-state='open']": {
    animation: `${fadeIn} 150ms cubic-bezier(0.16, 1, 0.3, 1), ${zoomIn} 150ms cubic-bezier(0.16, 1, 0.3, 1), ${slideInFromLeft} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  },
});

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  align = "center",
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        css={contentStyle}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
